import { playUIClick } from "@/audio/SoundManager";
import { AVATAR_TYPES, type AvatarType, type PlayerCustomization } from "./PlayerCustomization";

export class AvatarSelector {
  private container = document.getElementById("avatar-options")!;

  build(current: PlayerCustomization, onChange: (type: AvatarType) => void): void {
    for (const av of AVATAR_TYPES) {
      const btn = document.createElement("button");
      btn.className = "avatar-btn";
      btn.title = av.label;
      if (av.id === current.avatarType) btn.classList.add("selected");

      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 48;
      canvas.style.width = "32px";
      canvas.style.height = "48px";
      this.drawPreview(canvas, av.id);
      btn.appendChild(canvas);

      const label = document.createElement("span");
      label.className = "avatar-label";
      label.textContent = av.label;
      btn.appendChild(label);

      btn.addEventListener("click", () => {
        this.container.querySelectorAll("button").forEach((b) => b.classList.remove("selected"));
        btn.classList.add("selected");
        onChange(av.id);
        playUIClick();
      });
      this.container.appendChild(btn);
    }
  }

  private drawPreview(canvas: HTMLCanvasElement, type: AvatarType): void {
    const ctx = canvas.getContext("2d")!;
    const cx = 16;
    const feetY = 44;

    ctx.clearRect(0, 0, 32, 48);

    // Shadow
    ctx.fillStyle = "rgba(0,0,0,0.32)";
    ctx.beginPath();
    ctx.ellipse(cx, feetY, 11, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    if (type === "default") {
      ctx.fillStyle = "#cfcfcf";
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 1.5;
      this.roundedRect(ctx, cx - 7, feetY - 22, 14, 22, 4);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#e6e6e6";
      ctx.beginPath(); ctx.arc(cx, feetY - 30, 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke();

    } else if (type === "wizard") {
      ctx.fillStyle = "#b8b8d0";
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - 5, feetY - 24);
      ctx.lineTo(cx + 5, feetY - 24);
      ctx.lineTo(cx + 9, feetY);
      ctx.lineTo(cx - 9, feetY);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#e6e6e6";
      ctx.beginPath(); ctx.arc(cx, feetY - 30, 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#7070a0";
      ctx.beginPath();
      ctx.moveTo(cx, feetY - 44);
      ctx.lineTo(cx - 7, feetY - 28);
      ctx.lineTo(cx + 7, feetY - 28);
      ctx.closePath();
      ctx.fill(); ctx.stroke();

    } else if (type === "warrior") {
      ctx.fillStyle = "#c0c0b0";
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 1.5;
      this.roundedRect(ctx, cx - 9, feetY - 22, 18, 22, 3);
      ctx.fill(); ctx.stroke();

    } else if (type === "sorceress") {
      ctx.fillStyle = "#d0b8e0";
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - 4, feetY - 24);
      ctx.lineTo(cx + 4, feetY - 24);
      ctx.lineTo(cx + 10, feetY);
      ctx.lineTo(cx - 10, feetY);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#e6e6e6";
      ctx.beginPath(); ctx.arc(cx, feetY - 30, 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#c89af0";
      ctx.beginPath();
      ctx.moveTo(cx - 6, feetY - 32);
      ctx.quadraticCurveTo(cx - 8, feetY - 20, cx - 6, feetY - 14);
      ctx.lineTo(cx - 4, feetY - 14);
      ctx.quadraticCurveTo(cx - 6, feetY - 22, cx - 4, feetY - 30);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx + 6, feetY - 32);
      ctx.quadraticCurveTo(cx + 8, feetY - 20, cx + 6, feetY - 14);
      ctx.lineTo(cx + 4, feetY - 14);
      ctx.quadraticCurveTo(cx + 6, feetY - 22, cx + 4, feetY - 30);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#a0a090";
      ctx.fillRect(cx - 11, feetY - 20, 4, 6);
      ctx.fillRect(cx + 7, feetY - 20, 4, 6);
      ctx.strokeRect(cx - 11, feetY - 20, 4, 6);
      ctx.strokeRect(cx + 7, feetY - 20, 4, 6);
      ctx.fillStyle = "#e6e6e6";
      ctx.beginPath(); ctx.arc(cx, feetY - 30, 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#8a8a7a";
      ctx.beginPath();
      ctx.moveTo(cx - 7, feetY - 30);
      ctx.lineTo(cx - 7, feetY - 37);
      ctx.lineTo(cx + 7, feetY - 37);
      ctx.lineTo(cx + 7, feetY - 30);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
    }
  }

  private roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
}
